<template>
  <div>
    <div id="adjustable" class="bg-grey-1 shadow-2">
      <div class="row justify-between items-center no-wrap">
        <div
          class="q-py-sm"
          :class="$q.screen.xs ? 'q-pl-sm' : 'q-pl-md'"
          :style="$q.screen.gt.xs && 'width: 162px'"
        >
          <q-btn
            icon="arrow_back"
            color="primary"
            size="md"
            :padding="$q.screen.xs ? '6px 16px' : '6px 20px'"
            @click="$emit('back')"
          />
        </div>

        <div class="q-px-md">
          <div
            class="q-px-md text-center"
            :class="$q.screen.xs ? 'text-subtitle1' : 'text-h6'"
          >
            {{ Pond.name }}
          </div>

          <div class="row justify-center q-pb-sm">
            <q-btn
              v-if="GoToPond.previous || GoToPond.next"
              color="primary"
              icon="chevron_left"
              padding="1px 22px"
              size="md"
              class="q-mr-xs"
              :disabled="!GoToPond.previous"
              @click="$emit('goToPrevious')"
            />

            <q-btn
              v-if="GoToPond.previous || GoToPond.next"
              color="primary"
              icon="chevron_right"
              padding="1px 22px"
              size="md"
              class="q-ml-xs"
              :disabled="!GoToPond.next"
              @click="$emit('goToNext')"
            />
          </div>
        </div>

        <div class="q-py-sm" :class="$q.screen.xs ? 'q-pr-sm' : 'q-pr-md'">
          <q-btn
            :label="$q.screen.gt.xs ? 'New Flavor' : ''"
            color="primary"
            icon="add"
            size="md"
            @click="createFlavorPond"
          />
        </div>
      </div>

      <q-separator />
      <div class="row text-bold q-py-xs" :class="$q.screen.gt.xs && 'q-px-sm'">
        <div class="col-auto" style="width: 70px; padding-left: 20px">Date</div>
        <div class="col col-sm-3 text-center">Processor</div>
        <div class="col col-sm-3 text-center">Flavor</div>
        <div class="col-auto text-center" style="width: 60px">Severity</div>
        <div class="gt-xs col text-center">Note</div>
        <div class="gt-xs" style="width: 52px"></div>
        <div class="lt-sm" style="width: 28px"></div>
      </div>
    </div>

    <div
      v-if="History.length < 1"
      class="text-center text-h5 text-grey-6 q-px-md q-py-lg"
    >
      No Entries For This Pond
    </div>

    <q-virtual-scroll
      v-if="History.length"
      :style="`height: ${store.state.pageHeight - 146}px`"
      :items="History"
      separator
    >
      <template v-slot="{ item }">
        <div
          class="row items-center q-py-sm"
          :class="$q.screen.gt.xs && 'q-px-sm'"
        >
          <div class="col-auto q-pl-sm" style="width: 70px">
            {{ parseTimeStamp(item.date, 'short') }}
          </div>

          <div class="col col-sm-3 text-center" style="word-wrap: break-word">
            {{ decoder(item.processor) ?? '---' }}
          </div>

          <div class="col col-sm-3 text-center" style="word-wrap: break-word">
            {{ decoder(item.flavor_name) ?? '---' }}
          </div>

          <div
            class="col-auto text-center"
            style="word-wrap: break-word; width: 60px"
          >
            {{ item.severity }}
          </div>

          <div class="gt-xs col text-center">
            {{ item.memo }}
          </div>

          <div class="gt-xs q-pr-sm">
            <q-btn
              unelevated
              icon="edit"
              color="grey-3"
              text-color="accent"
              padding="2px 10px"
              @click="editFlavor(item)"
            />
          </div>

          <div class="lt-sm q-pr-sm">
            <q-btn
              outline
              icon="more_vert"
              color="primary"
              padding="4px 4px"
              size="sm"
              @click="displayHistoryItem(item)"
            >
            </q-btn>
          </div>
        </div>
        <q-separator />
      </template>
    </q-virtual-scroll>

    <!-- Line Entry Dialog -->
    <q-dialog position="right" full-hight v-model="dialogHistory">
      <q-card style="width: min(400px, 90vw)">
        <div class="row items-center q-pa-sm">
          <div class="col">
            <div class="text-center text-subtitle1">
              {{ parseTimeStamp(historyObj?.date || '', 'short') }}
            </div>
          </div>
        </div>
        <q-separator style="padding-top 1px" />

        <div>
          <div class="row justify-between q-pa-sm">
            <div>Processor</div>
            <div>
              {{ decoder(historyObj.processor) ?? '' }}
            </div>
          </div>
          <q-separator />

          <div class="row justify-between q-pa-sm">
            <div>Flavor</div>
            <div>
              {{ decoder(historyObj.flavor_name) ?? '' }}
            </div>
          </div>
          <q-separator />

          <div class="row justify-between q-pa-sm">
            <div>Severity</div>
            <div>
              {{ historyObj.severity ?? '' }}
            </div>
          </div>
          <q-separator />

          <div class="row items-center q-pa-sm">
            <div class="text-weight-medium q-pr-sm" style="padding-bottom: 2px">
              Note:
            </div>
            <div>
              {{ decoder(historyObj.memo) }}
            </div>
          </div>
          <q-separator />

          <div class="row justify-end q-py-sm q-pr-sm">
            <q-btn
              unelevated
              label="Close"
              color="grey-4"
              text-color="black"
              class="q-mr-sm"
              v-close-popup
            />

            <div v-if="historyObj.transfer_type !== 'Transfer In'">
              <q-btn
                unelevated
                label="Edit"
                color="grey-4"
                text-color="accent"
                @click="editFlavor(historyObj)"
              />
            </div>
          </div>
        </div>
      </q-card>
    </q-dialog>

    <!-- Flavor Pond Dialog -->
    <q-dialog persistent full-height position="top" v-model="dialogFlavorPond">
      <FlavorPond
        :Action="action"
        :Flavor="flavor"
        :Pond="Pond"
        @close="dialogFlavorPond = false"
      />
    </q-dialog>

    <!-- Notes Dialog -->
    <q-dialog position="right" full-hight v-model="dialogNote">
      <q-card style="width: 300px">
        <div class="row items-center q-pa-sm">
          <div class="col">
            <div class="text-center text-subtitle1">
              {{ parseTimeStamp(noteDate || '', 'short') }}
            </div>
          </div>
          <q-btn
            unelevated
            icon="close"
            color="grey-3"
            text-color="black"
            size="md"
            padding="2px 10px"
            v-close-popup
          />
        </div>
        <q-separator style="padding-top 1px" />

        <div class="q-pa-sm">
          <span class="text-subtitle2 q-pr-sm"> Note: </span>
          {{ decoder(note) }}
        </div>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import FlavorPond from '@/components/ponds/FlavorPond.vue';

import { parseTimeStamp } from '@/lib/date-utils.js';
import { getUID, guidMatchHelper } from '@/store/transactionHelpers';
import store from '@/store';
import {
  decoder,
  numberWithCommas,
  isNumValid,
  cloneObj,
  getPercentage,
  getScrollbarWidth
} from '@/lib/helpers';
import { isDateValid, setTimeOfDay } from '@/lib/date-utils.js';
import { date, uid } from 'quasar';

function defaultFlavor() {
  return {
    id: null,
    user_id: store.state.user.id,
    proc_id: null,
    flavor: null,
    severity: 0,
    date: +new Date() / 1000,
    memo: '',
    guid: null,
    storeInfo: {
      farmId: store.state.selectedFarm.farm_id
    }
  };
}

export default {
  name: 'FlavorDetail',
  components: {
    FlavorPond
  },
  props: {
    Pond: {
      type: Object,
      required: true
    },
    GoToPond: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      action: 'create',
      dateCopy: null,
      dateCopyStr: null,
      decoder,
      dialogHistory: false,
      dialogNote: false,
      dialogFlavorPond: false,
      historyObj: {},
      isDateValid,
      isNumValid,
      note: '',
      noteDate: '',
      parseTimeStamp,
      flavor: defaultFlavor(),
      store
    };
  },
  // mounted() {},
  methods: {
    createFlavorPond() {
      this.action = 'create';
      this.flavor = defaultFlavor();

      this.dialogFlavorPond = true;
    },
    displayHistoryItem(item) {
      this.historyObj = item;
      this.dialogHistory = true;
    },
    editFlavor(flavor) {
      this.action = 'update';

      this.flavor = {
        ...defaultFlavor(),
        ...flavor
      };

      this.dialogFlavorPond = true;
    }
  },
  computed: {
    History() {
      return this.Pond.flavorHistory;
    }
  }
};
</script>

<style></style>
